import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { usePlayer } from "./PlayerProvider";
import IconButton from "../components/IconButton";
import { PlayIcon, PauseIcon, ChevronUpIcon } from "../components/icons";
import { RotateBackGlyph } from "./transportGlyphs";

/*
 * Persistent mini-player — collapsed bar per the Class Page reference:
 * [video thumbnail (video mode)] [48px play] [title + full-width progress —
 * tapping it expands] [−10s] [expand chevron]. All controls ≥44px.
 * Renders nothing until a track loads. The bottom sheet is ExpandedSheet.
 */

export function fmt(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export default function MiniPlayer() {
  const player = usePlayer();
  const current = useSelector((s) => s.player.current);
  const playing = useSelector((s) => s.player.playing);
  const position = useSelector((s) => s.player.position);
  const duration = useSelector((s) => s.player.duration);
  const expanded = useSelector((s) => s.player.expanded);
  // Redundant while the page's own player card for this class is on screen.
  const cardOnScreen = useSelector((s) => s.player.cardOnScreen);

  // Claim the live video for the thumbnail while the bar is showing a video
  // track. Priority 1 — lowest: on the class page the player card outranks
  // it (the thumb shows the poster fallback), but once the user navigates
  // away the card releases and the video plays on right here in the bar.
  const thumbRef = useRef(null);
  useEffect(() => {
    if (!current || expanded || cardOnScreen || current.mediaKind !== "video" || !thumbRef.current) return undefined;
    return player.claimVideo(thumbRef.current, 1);
  }, [current, expanded, cardOnScreen, player]);

  if (!current || expanded || cardOnScreen) return null;

  const pct = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;
  const isVideo = current.mediaKind === "video";
  const title = current.seriesTitle ? `${current.title} · ${current.seriesTitle}` : current.title;

  return (
    <div className="ic-scope fixed bottom-0 inset-x-0 z-50 bg-ic-navy border-t border-white/10 shadow-[0px_-4px_12px_rgba(0,0,0,0.3)] font-sans">
      <div className="max-w-[1280px] mx-auto px-4 py-2.5 flex items-center gap-2.5">
        {isVideo && (
          <button
            type="button"
            aria-label="Expand video player"
            onClick={() => player.setExpanded(true)}
            className="flex-shrink-0 relative w-[85px] h-12 rounded-md overflow-hidden bg-[#0D1420] border border-white/20 hidden xs:flex flex-col items-center justify-center cursor-pointer p-0 transition-colors duration-150 ease-out hover:border-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
          >
            {current.poster ? (
              <img src={current.poster} alt="" className="w-full h-full object-cover" />
            ) : (
              <>
                <span className="text-[13px] font-extrabold text-ic-sky opacity-70 uppercase leading-4">Video</span>
                <span className="text-[7px] font-bold tracking-[0.5px] text-ic-lightGray opacity-70">TAP TO EXPAND</span>
              </>
            )}
            {/* Live video mount — fills the thumb when this bar holds the
                claim; empty (poster/fallback shows through) when a
                higher-priority surface has the video. */}
            <span ref={thumbRef} aria-hidden="true" className="absolute inset-0 pointer-events-none [&>video]:pointer-events-none" />
            {!playing && (
              <span className="absolute inset-0 bg-[rgba(22,30,46,0.6)] flex items-center justify-center text-white">
                <PlayIcon size={16} />
              </span>
            )}
          </button>
        )}

        <IconButton variant="solid" dark size={48} label={playing ? "Pause" : "Play"} onClick={player.toggle}>
          {playing ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
        </IconButton>

        {/* Title + progress — tapping expands the sheet */}
        <button
          type="button"
          aria-label="Expand player"
          onClick={() => player.setExpanded(true)}
          className="flex-1 min-w-0 flex flex-col gap-1 bg-transparent border-0 p-0 font-sans text-left cursor-pointer rounded-lg focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
        >
          <span className="text-[14px] font-semibold leading-5 text-ic-offwhite whitespace-nowrap overflow-hidden text-ellipsis max-w-full block">
            {title}
          </span>
          <span className="flex items-center gap-2 w-full">
            <span className="hidden xs:inline text-[12px] leading-4 text-ic-lightGray tabular-nums flex-shrink-0">{fmt(position)}</span>
            <span className="flex-1 h-[3px] rounded-sm bg-white/20 overflow-hidden block">
              <span className="block h-full bg-ic-sky" style={{ width: `${pct}%` }} />
            </span>
            <span className="hidden xs:inline text-[12px] leading-4 text-ic-lightGray tabular-nums flex-shrink-0">
              {fmt(duration || current.durationHint || 0)}
            </span>
          </span>
        </button>

        <IconButton variant="bareDark" size={44} dark label="Back 10 seconds" onClick={() => player.seekBy(-10)}>
          <RotateBackGlyph size={22} />
        </IconButton>
        <IconButton
          variant="bareDark"
          size={44}
          dark
          label="Expand player for speed, skip, and volume controls"
          onClick={() => player.setExpanded(true)}
        >
          <ChevronUpIcon size={20} />
        </IconButton>
      </div>
    </div>
  );
}
